<template>
    <div class="project-page-container">
        <div class="project-hero">
            <img class="project-hero-img dashboard-hero-img" src="../assets/dashboard.png" alt="Quote Dashboard">
            <h1 class="project-h1">myBLDR Quote Dashboard</h1>
            <div class="project-hero-subtext">
                <p class="project-contribution">Enterprise UX Design</p>
                <p class="project-contribution">Data Visualization</p>
                <p class="project-contribution">Research-Driven Iteration</p>
            </div>
        </div>
        <div class="project-background light-green">
            <div class="project-background-inner-container">
                <h2 class="project-background-h2 project-description-h2">
                    At Paradigm, I redesigned the quote dashboard inside myBLDR, an enterprise order-processing and project-management platform used by building-supply sales reps and builders.
                    <br><br>
                    I led design from research through handoff: replacing a fragmented, filter-heavy interface with a unified search experience, AI-powered pipeline insights, and a calendar timeline.  This cut time-on-task for quote management by <span class="bold">85%</span> and boosted daily active usage by <span class="bold">62%</span>.
                </h2>
            </div>
        </div>

        <!-- Business Impact -->
        <div class="project-background">
            <div class="project-background-inner-container">
                <h2 class="project-background-h2">End Result & Business Impact</h2>
                <div class="business-impact">
                    <div class="business-impact-imgs">
                        <img class="dashboard-web-mockup" src="../assets/dashboard-search.png" alt="Quote Dashboard Search">
                    </div>
                    <div class="business-impact-text">
                        <div>
                            <div>&bull;</div>
                            <div>Reduced time-on-task for searching and filtering quotes by <span class="bold">85%</span>, directly improving sales rep productivity.</div>
                        </div>
                        <div>
                            <div>&bull;</div>
                            <div>Increased quote-to-close conversion by surfacing pipeline health and actionable insights at a glance.</div>
                        </div>
                        <div>
                            <div>&bull;</div>
                            <div>Cut new rep onboarding time by <span class="bold">43%</span>. The redesigned dashboard made workflows intuitive from day one.</div>
                        </div>
                        <div>
                            <div>&bull;</div>
                            <div>Boosted daily active usage by <span class="bold">62%</span>, validating sustained adoption across sales teams.</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Ecosystem Context -->
        <div class="project-background-inner-container">
            <h2 class="project-background-h2">Understanding the Ecosystem</h2>
            <p class="project-background-p">
                Before designing a single screen, I mapped how the dashboard fit within myBLDR and the broader Paradigm suite. The dashboard sits at the center of the sales rep's workflow.  It's the entry point that connects to individual quotes, the Estimate engine, and ultimately the Builder's order creation. Designing without understanding these dependencies would have produced something that looked right but failed in practice.
            </p>
            <div class="project-img-container" style="margin-top: 24px; margin-bottom: 12px;">
                <img class="dashboard-visual-sample ecosystem-diagram" style="border:1px solid #e0e0e0;" src="../assets/paradigm-ecosystem.svg" alt="Paradigm myBLDR Ecosystem Diagram showing the relationship between the Quote Dashboard, Individual Quotes, Estimate, Orders, and the Sales Rep and Builder end users">
            </div>
            <p class="caption-p">The Quote Dashboard is the primary workspace for Sales Reps. It connects to individual quotes, a two-way Estimate engine for material pricing, and downstream order creation by Builders.</p>
        </div>

        <!-- Problem -->
        <div class="project-background-inner-container not-first-cream-background">
            <h2 class="project-background-h2">Problem</h2>
            <p class="project-background-p problem-p">
                Sales reps were losing time managing thousands of quotes in a dashboard that worked against them.
            </p>
            <div class="business-impact">
                <div class="business-impact-text">
                    <div>
                        <div>&bull;</div>
                        <div>Search filters behaved inconsistently (sometimes client-side, sometimes server-side) with no visual feedback to indicate which was active.</div>
                    </div>
                    <div>
                        <div>&bull;</div>
                        <div>Filter controls dominated vertical space, pushing the actual quote data below the fold and forcing constant scrolling.</div>
                    </div>
                </div>
            </div>
            <div class="business-impact-imgs dashboard-before" style="margin-top: 20px;">
                <img class="dashboard-web-mockup" src="../assets/dashboard-before.png" alt="Quote Dashboard before redesign">
            </div>
            <p class="caption-p">The original quote search. Filter controls monopolized screen real estate, leaving little room for the data reps actually needed.</p>
        </div>

        <!-- Goals & Deliverables -->
        <div class="project-background-inner-container not-first-cream-background">
            <h2 class="project-background-h2">Goals & Deliverables</h2>
            <p class="project-background-p">
                1. <span class="bold-span">Omni Search:</span> A unified, predictable search bar with consistent filtering behavior and immediate visual feedback. No more guessing whether a filter was applied.
                <br><br>
                2. <span class="bold-span">AI-Driven Data Visuals:</span> Embedded graphs and pipeline insights surfacing anomalies and performance signals without requiring reps to dig for them.
                <br><br>
                3. <span class="bold-span">Calendar Integration:</span> A visual timeline making upcoming quote actions and follow-up deadlines immediately visible.
            </p>
        </div>

        <!-- Approach / Process -->
        <div class="project-background-inner-container not-first-cream-background">
            <h2 class="project-background-h2">Design Process</h2>
            <div class="business-impact">
                <div class="business-impact-text">
                    <div>
                        <div>&bull;</div>
                        <div><span class="bold">Discovery:</span> Conducted user interviews with sales reps to map pain points, mental models, and daily workflow patterns. Used findings to write clear experience requirements before touching Figma.</div>
                    </div>
                    <div>
                        <div>&bull;</div>
                        <div><span class="bold">Cross-functional alignment:</span> Facilitated working sessions with product and engineering to pressure-test requirements against technical constraints and business goals.  This resolved competing priorities before they became design debt.</div>
                    </div>
                    <div>
                        <div>&bull;</div>
                        <div><span class="bold">Wireframes → Prototypes:</span> Iterated from low-fidelity wireframes through interactive Figma prototypes. Used prototypes to validate search flow, visual hierarchy, and calendar interactions with actual reps before committing to high-fidelity.</div>
                    </div>
                    <div>
                        <div>&bull;</div>
                        <div><span class="bold">High-fidelity design & system alignment:</span> Built final designs within Paradigm's design system, contributing reusable dashboard components and data visualization patterns that extended to other parts of the suite.</div>
                    </div>
                    <div>
                        <div>&bull;</div>
                        <div><span class="bold">User testing & iteration:</span> Validated designs with reps through moderated testing sessions. Refined load-state handling, visual hierarchy, and filter affordances based on direct feedback before handoff.</div>
                    </div>
                    <div>
                        <div>&bull;</div>
                        <div><span class="bold">Handoff & implementation support:</span> Delivered annotated specs and component documentation in Figma. Stayed engaged during development to clarify design intent and catch implementation drift before it shipped.</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Visual Samples -->
        <div class="project-background-inner-container not-first-cream-background">
            <h2 class="project-background-h2">Visual Samples</h2>
            <div class="project-img-container" style="margin-top: 20px;">
                <img class="dashboard-visual-sample" src="../assets/dashboard-calendar.png" alt="Calendar view showing upcoming quote actions">
            </div>
            <div class="project-img-container">
                <img class="dashboard-visual-sample" src="../assets/dashboard-results.png" alt="Dashboard results view">
            </div>
            <div class="project-img-container">
                <img class="dashboard-visual-sample" src="../assets/dashboard-example.png" alt="Dashboard with AI-powered data visualizations">
            </div>
            <div class="project-img-container">
                <img class="dashboard-visual-sample" src="../assets/dashboard-example-2.png" alt="Dashboard pipeline health view">
            </div>
        </div>

        <div class="project-background-inner-container not-first-cream-background">
            <h2 class="project-background-h2">Thanks for reading!</h2>
        </div>
        <div class="project-background-inner-container not-first-cream-background back-to-top-container">
            <a @click="scrollToTop">Back to top</a>
            <router-link to="/projects">More projects</router-link>
        </div>
    </div>
</template>

<script>
export default {
    name: 'DashboardProjectPage',
    data: function() {
        return {};
    },
    methods: {
        scrollToTop() {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    }
}
</script>